import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { api, errMsg } from '@/lib/api';

const STARTER = `<!doctype html>
<html><body style="margin:0;background:#f8f9fc;font-family:system-ui,-apple-system,sans-serif;color:#1e293b">
<table role="presentation" width="100%" cellspacing="0" cellpadding="0"><tr><td align="center" style="padding:32px 16px">
<table role="presentation" width="560" cellspacing="0" cellpadding="0" style="background:#fff;border:1px solid #e8ecf2;border-radius:12px">
<tr><td style="padding:32px;font-size:22px;font-weight:600">Hi {{first_name}},</td></tr>
<tr><td style="padding:0 32px 24px;font-size:15px;line-height:1.6;color:#334155">Write your newsletter here.</td></tr>
<tr><td style="padding:0 32px 32px"><a href="https://facetflow.ai" style="display:inline-block;background:#0E56A8;color:#fff;text-decoration:none;padding:12px 20px;border-radius:8px">Open Facet Flow</a></td></tr>
<tr><td style="padding:0 32px 28px;font-size:12px;color:#94a3b8"><a href="{{unsubscribe_url}}" style="color:#94a3b8">Unsubscribe</a></td></tr>
</table></td></tr></table></body></html>`;

export function ImportForm() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [kind, setKind] = useState('newsletter');
  const [html, setHtml] = useState(STARTER);
  const [zip, setZip] = useState<{ name: string; base64: string; size: number } | null>(null);
  const [report, setReport] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (zip) {
        const created = await api<{ id: string; import: { imagesUploaded: number; referencesRewritten: number; missingReferences: string[]; hasUnsubscribe: boolean } }>('/templates/import-zip', {
          method: 'POST',
          json: { name, subject, preheader: preheader || undefined, kind, zipBase64: zip.base64 },
        });
        const i = created.import;
        setReport(`Uploaded ${i.imagesUploaded} image(s), rewrote ${i.referencesRewritten} reference(s)${i.missingReferences.length ? `; missing: ${i.missingReferences.join(', ')}` : ''}${i.hasUnsubscribe ? '' : '. No {{unsubscribe_url}} found — add one before using it for marketing.'}`);
        navigate(`/templates/${created.id}`);
        return;
      }
      const created = await api<{ id: string }>('/templates', { method: 'POST', json: { name, subject, preheader: preheader || undefined, kind, html } });
      navigate(`/templates/${created.id}`);
    } catch (err) {
      setError(errMsg(err, 'Import failed'));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="grid gap-4 rounded-lg border bg-white p-4 text-sm lg:grid-cols-[320px_1fr]">
      <div className="space-y-3">
        <label className="block space-y-1">
          <span className="text-xs text-muted-foreground">Name</span>
          <input required value={name} onChange={(e) => setName(e.target.value)} className="h-9 w-full rounded-md border px-2" />
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-muted-foreground">Subject (merge tags allowed)</span>
          <input required value={subject} onChange={(e) => setSubject(e.target.value)} className="h-9 w-full rounded-md border px-2" />
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-muted-foreground">Preheader</span>
          <input value={preheader} onChange={(e) => setPreheader(e.target.value)} className="h-9 w-full rounded-md border px-2" />
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-muted-foreground">Kind</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-9 w-full rounded-md border px-2">
            <option value="newsletter">Newsletter (marketing domain)</option>
            <option value="flow">Flow email (marketing domain)</option>
            <option value="transactional">Transactional (product domain, exempt from caps)</option>
          </select>
        </label>
        <label className="block space-y-1 rounded-md border border-dashed p-2">
          <span className="text-xs text-muted-foreground">Upload .zip (HTML + images folder) — images are uploaded to Firebase Storage for you and the paths rewritten</span>
          <input
            type="file"
            accept=".zip,application/zip"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return setZip(null);
              const base64 = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
                reader.readAsDataURL(f);
              });
              setZip({ name: f.name, base64, size: f.size });
            }}
          />
          {zip && <span className="block text-xs">{zip.name} · {(zip.size / 1024).toFixed(0)} KB — the HTML box below is ignored</span>}
        </label>
        <label className="block space-y-1">
          <span className="text-xs text-muted-foreground">…or upload .html</span>
          <input
            type="file"
            accept=".html,.htm,text/html"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (f) setHtml(await f.text());
            }}
          />
        </label>
        <Button type="submit" disabled={busy || (!zip && !html.trim())}>
          {busy ? (zip ? 'Uploading images & importing…' : 'Importing…') : zip ? 'Import zip' : 'Import'}
        </Button>
        {report && <p className="rounded-md bg-emerald-50 p-2 text-xs text-emerald-800">{report}</p>}
        {error && (
          <p role="alert" className="rounded-md bg-red-50 p-2 text-xs text-red-800">
            {error}
          </p>
        )}
      </div>
      <label className="block space-y-1">
        <span className="text-xs text-muted-foreground">HTML</span>
        <textarea value={html} onChange={(e) => setHtml(e.target.value)} rows={24} className="w-full rounded-md border p-2 font-mono text-xs" />
      </label>
    </form>
  );
}
